import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useCache } from '../context/CacheContext';
import { cachedDashboardService } from '../services/cachedApiService';
import '../styles/AdminOverview.css';

interface OverviewStats {
  total_patients: number;
  total_providers: number;
  total_pa_requests: number;
  total_encounters: number;
}

const AdminOverview: React.FC = () => {
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { invalidate } = useCache();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        
        // Fetch real data from the API
        const response = await cachedDashboardService.getDashboardStats();
        
        // Map the response to our OverviewStats interface
        const realStats: OverviewStats = {
          total_patients: response.total_patients || 0,
          total_providers: response.total_providers || 0,
          total_pa_requests: response.total_pa_requests || 0,
          total_encounters: response.total_encounters || 0
        };
        
        setStats(realStats);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setError('Failed to fetch overview statistics');
        setLoading(false);
      }
    };

    if (user?.role === 'superuser') {
      fetchStats();
    } else {
      setError('You do not have permission to view this page');
      setLoading(false);
    }
  }, [user]);

  if (loading) {
    return (
      <Layout>
        <div className="loading" style={{ padding: '40px', textAlign: 'center' }}>
          <div className="loading-spinner" style={{ width: '50px', height: '50px', margin: '0 auto 20px auto' }}></div>
          <div>Loading overview statistics...</div>
        </div>
      </Layout>
    );
  }

  if (error || !stats) {
    return (
      <Layout>
        <div className="error" style={{ padding: '20px', textAlign: 'center', color: '#e53e3e', backgroundColor: '#fff5f5', borderRadius: '8px', margin: '20px 0' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ marginBottom: '10px' }}>
            <path d="M12 9V13M12 17H12.01M5.07183 19H18.9282C20.4678 19 21.4301 17.3333 20.6603 16L13.7321 4C12.9623 2.66667 11.0378 2.66667 10.268 4L3.33978 16C2.56998 17.3333 3.53223 19 5.07183 19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div>{error || 'Failed to load data'}</div>
          <button
            onClick={() => {
              invalidate.dashboard();
              window.location.reload();
            }}
            style={{
              marginTop: '15px',
              padding: '8px 16px',
              backgroundColor: '#3182ce',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="admin-overview-container">
        <h2 className="mb-4 d-flex align-items-center justify-content-center gap-2">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M8 13V17M12 9V17M16 5V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span style={{ background: 'linear-gradient(45deg, #3182ce, #4299e1)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontSize: '1.75rem' }}>
            System Overview
          </span>
        </h2>
        
        <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '30px' }}>
          <div className="stat-card" style={{ padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', background: 'linear-gradient(135deg, #f0f9ff 0%, #e6f7ff 100%)', borderLeft: '4px solid #3182ce' }}>
            <div className="stat-icon" style={{ marginBottom: '15px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#3182ce" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="#3182ce" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="#3182ce" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="#3182ce" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="stat-value" style={{ fontSize: '2rem', fontWeight: '700', color: '#2c5282' }}>{stats.total_patients}</div>
            <div className="stat-label" style={{ fontSize: '0.9rem', color: '#4a5568', marginBottom: '10px' }}>Total Patients</div>
            <Link to="/patients" className="stat-link" style={{ fontSize: '0.85rem', color: '#3182ce', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
              View All
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '4px' }}>
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
          
          <div className="stat-card" style={{ padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', background: 'linear-gradient(135deg, #f0fff4 0%, #e6fffa 100%)', borderLeft: '4px solid #38a169' }}>
            <div className="stat-icon" style={{ marginBottom: '15px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 4H18C18.5304 4 19.0391 4.21071 19.4142 4.58579C19.7893 4.96086 20 5.46957 20 6V20C20 20.5304 19.7893 21.0391 19.4142 21.4142C19.0391 21.7893 18.5304 22 18 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4142C4.21071 21.0391 4 20.5304 4 20V6C4 5.46957 4.21071 4.96086 4.58579 4.58579C4.96086 4.21071 5.46957 4 6 4H8" stroke="#38a169" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M15 2V6" stroke="#38a169" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 2V6" stroke="#38a169" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 11C13.6569 11 15 9.65685 15 8C15 6.34315 13.6569 5 12 5C10.3431 5 9 6.34315 9 8C9 9.65685 10.3431 11 12 11Z" stroke="#38a169" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 16H16" stroke="#38a169" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 11V19" stroke="#38a169" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="stat-value" style={{ fontSize: '2rem', fontWeight: '700', color: '#276749' }}>{stats.total_providers}</div>
            <div className="stat-label" style={{ fontSize: '0.9rem', color: '#4a5568', marginBottom: '10px' }}>Total Providers</div>
            <Link to="/practitioners" className="stat-link" style={{ fontSize: '0.85rem', color: '#38a169', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
              View All
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '4px' }}>
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
          
          <div className="stat-card" style={{ padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', background: 'linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%)', borderLeft: '4px solid #e53e3e' }}>
            <div className="stat-icon" style={{ marginBottom: '15px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#e53e3e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2V8H20" stroke="#e53e3e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 13H8" stroke="#e53e3e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 17H8" stroke="#e53e3e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 9H9H8" stroke="#e53e3e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="stat-value" style={{ fontSize: '2rem', fontWeight: '700', color: '#c53030' }}>{stats.total_pa_requests}</div>
            <div className="stat-label" style={{ fontSize: '0.9rem', color: '#4a5568', marginBottom: '10px' }}>PA Requests</div>
            <Link to="/prior-authorizations" className="stat-link" style={{ fontSize: '0.85rem', color: '#e53e3e', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
              View All
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '4px' }}>
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
          
          <div className="stat-card" style={{ padding: '20px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', background: 'linear-gradient(135deg, #ebf8ff 0%, #bee3f8 100%)', borderLeft: '4px solid #4299e1' }}>
            <div className="stat-icon" style={{ marginBottom: '15px' }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" stroke="#4299e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 2V6" stroke="#4299e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 2V6" stroke="#4299e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3 10H21" stroke="#4299e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 14H8.01" stroke="#4299e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 14H12.01" stroke="#4299e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 14H16.01" stroke="#4299e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 18H8.01" stroke="#4299e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 18H12.01" stroke="#4299e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 18H16.01" stroke="#4299e1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="stat-value" style={{ fontSize: '2rem', fontWeight: '700', color: '#2b6cb0' }}>{stats.total_encounters}</div>
            <div className="stat-label" style={{ fontSize: '0.9rem', color: '#4a5568', marginBottom: '10px' }}>Total Encounters</div>
            <div style={{ fontSize: '0.85rem', color: '#4299e1', display: 'inline-flex', alignItems: 'center' }}>
              Last 30 Days
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '4px' }}>
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
        
        <div className="back-btn" style={{ marginTop: '20px', textAlign: 'center' }}>
          <Link to="/dashboard" style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '8px 16px',
            borderRadius: '30px',
            background: 'white',
            color: '#4a5568',
            textDecoration: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e2e8f0',
            fontSize: '14px',
            fontWeight: '500'
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginRight: '6px' }}>
              <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Dashboard
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default AdminOverview;